import type {
  OrgMemberSummary,
  OrgModelPermissionMode,
  OrgUnitSummary,
} from '@nadoutong/contracts';
import { useState } from 'react';
import { ModelPermissionsModal } from '../../components/ModelPermissionsModal.tsx';
import type { ModelPermissionTarget } from '../../components/ModelPermissionsModal.tsx';
import { useT } from '../../i18n/index.ts';
import { Button, Chip, EmptyState, Panel } from '../../ui/index.ts';
import { modelPermissionModeLabelKey, modelPermissionModeTone } from './modelPermissionMode.ts';

/**
 * Model permissions — `/organization?tab=model_permissions`. First-class
 * sibling of the roles matrix (`?tab=permissions`), not an appendix
 * under it. Ported from console-web's routed `/team/permissions` page,
 * epic #1 PR-4.
 *
 * Two independent lists (members / org units), each row carrying its
 * current mode chip; Configure opens the shared, target-parameterized
 * `ModelPermissionsModal`. Only member-role accounts are listed: owners
 * and admins are not subject to model permissions, so offering them a
 * configure button would promise an effect that does not exist.
 *
 * One departure from the source page: members and org units are already
 * loaded once by `/organization` for the other tabs, so this panel takes
 * them as props instead of issuing its own queries. Loading, load errors
 * (with retry) and refresh therefore live in the page chrome — a panel
 * that re-queried would duplicate both the request and the error report.
 *
 * Permission gating: `org:members:read` gates the whole page, and
 * without `org:members:manage` every Configure button is disabled, so
 * the modal never mounts and no permission request is ever issued.
 */

interface Props {
  readonly orgId: string | undefined;
  readonly members: readonly OrgMemberSummary[];
  readonly units: readonly OrgUnitSummary[];
  readonly canManage: boolean;
}

export function PermissionsPanel({ orgId, members, units, canManage }: Props): JSX.Element {
  const t = useT();
  const [target, setTarget] = useState<ModelPermissionTarget | null>(null);
  const memberRows = members.filter((member) => member.role === 'member');

  return (
    <div data-testid="organization-permissions" className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Panel
          caption={t('organization.permissions.members.title')}
          count={memberRows.length}
          testId="organization-permissions-members"
        >
          {memberRows.length === 0 ? (
            <EmptyState
              testId="organization-permissions-members-empty"
              title={t('organization.permissions.members.empty')}
              hint={t('organization.permissions.members.empty_hint')}
            />
          ) : (
            <ul className="m-0 flex list-none flex-col p-0">
              {memberRows.map((member) => (
                <PermissionRow
                  key={member.accountId}
                  title={member.displayName ?? member.email}
                  detail={member.email}
                  mode={member.modelPermissionMode}
                  canManage={canManage}
                  configureLabel={t('organization.permissions.configure')}
                  modeLabel={t(modelPermissionModeLabelKey(member.modelPermissionMode))}
                  rowTestId={`organization-permissions-member-${member.accountId}`}
                  buttonTestId={`organization-permissions-configure-member-${member.accountId}`}
                  onConfigure={() => setTarget({ kind: 'member', member })}
                />
              ))}
            </ul>
          )}
        </Panel>

        <Panel
          caption={t('organization.permissions.units.title')}
          count={units.length}
          testId="organization-permissions-units"
        >
          {units.length === 0 ? (
            <EmptyState
              testId="organization-permissions-units-empty"
              title={t('organization.permissions.units.empty')}
              hint={t('organization.permissions.units.empty_hint')}
            />
          ) : (
            <ul className="m-0 flex list-none flex-col p-0">
              {units.map((unit) => (
                <PermissionRow
                  key={unit.id}
                  title={unit.name}
                  detail={unit.path}
                  mode={unit.modelPermissionMode}
                  canManage={canManage}
                  configureLabel={t('organization.permissions.configure')}
                  modeLabel={t(modelPermissionModeLabelKey(unit.modelPermissionMode))}
                  rowTestId={`organization-permissions-unit-${unit.id}`}
                  buttonTestId={`organization-permissions-configure-unit-${unit.id}`}
                  onConfigure={() => setTarget({ kind: 'unit', unit })}
                />
              ))}
            </ul>
          )}
        </Panel>
      </div>

      {target !== null && orgId !== undefined && canManage ? (
        <ModelPermissionsModal orgId={orgId} target={target} onClose={() => setTarget(null)} />
      ) : null}
    </div>
  );
}

interface PermissionRowProps {
  readonly title: string;
  readonly detail: string;
  readonly mode: OrgModelPermissionMode;
  readonly modeLabel: string;
  readonly canManage: boolean;
  readonly configureLabel: string;
  readonly rowTestId: string;
  readonly buttonTestId: string;
  readonly onConfigure: () => void;
}

function PermissionRow({
  title,
  detail,
  mode,
  modeLabel,
  canManage,
  configureLabel,
  rowTestId,
  buttonTestId,
  onConfigure,
}: PermissionRowProps): JSX.Element {
  return (
    <li
      data-testid={rowTestId}
      className="flex items-center justify-between gap-3 border-b border-line-soft px-4 py-3 last:border-b-0"
    >
      <span className="min-w-0">
        <span className="block truncate text-body font-medium text-ink">{title}</span>
        <span className="block truncate font-mono text-caption text-ink-low">{detail}</span>
      </span>
      <span className="flex shrink-0 items-center gap-2">
        <Chip tone={modelPermissionModeTone(mode)} lamp={false} testId={`${rowTestId}-mode`}>
          {modeLabel}
        </Chip>
        <Button
          variant="ghost"
          size="sm"
          disabled={!canManage}
          onClick={onConfigure}
          data-testid={buttonTestId}
        >
          {configureLabel}
        </Button>
      </span>
    </li>
  );
}
